/**
 * S11 · Our clients (#clients) — THE FEED homepage, design.md Part III.
 * FINAL-SPEC §4 S11. The markup already holds every logo, all visible (no-JS
 * and the SEO view). This adds:
 *  - the filter chips (button[aria-pressed]): "All" or one sector; the others
 *    get `hidden`; a swap crossfades out (150ms) and pops the new set in;
 *  - per-group "Show all" expanders (button[aria-expanded]): in "All", each group
 *    shows its first 2 rows; the rest stay in the DOM as `.is-over`
 *    (display: none, so their lazy images do not load yet);
 *  - the wall fold: in "All", only the first LEAD groups show; the rest are
 *    `.is-later` behind one "Show all sectors" button (button[data-cl-wall]),
 *    so the section stays ~2–3 screens tall instead of five;
 *  - the diagonal wave: each tile's `--w` = row + col (capped) for the reveal.
 * Reduced motion: swaps and expansions are instant (no crossfade, no pop).
 */
import { reduced } from "./runtime";

const WIDE = "(min-width: 900px)";
/** groups shown in "All" before the wall-level "Show all sectors" fold */
const LEAD = 4;
/** rows a group shows in "All" before its own "Show all" */
const ROWS = 2;

interface Group {
  el: HTMLElement;
  key: string;
  list: HTMLElement;
  tiles: HTMLElement[];
  more: HTMLButtonElement | null;
  expanded: boolean;
  cols: number;
}

function columns(list: HTMLElement): number {
  const tpl = getComputedStyle(list).gridTemplateColumns;
  const n = tpl.split(" ").filter((s) => s && s !== "none").length;
  return Math.max(1, n);
}

/** Pop a set of tiles in (after a filter swap or an expansion). */
function popIn(tiles: HTMLElement[]): void {
  if (reduced()) return;
  tiles.forEach((tile, k) => {
    tile.classList.remove("is-new");
    tile.style.setProperty("--k", String(Math.min(k, 25)));
  });
  // restart the animation on tiles that ran it before
  void document.body.offsetWidth;
  for (const tile of tiles) tile.classList.add("is-new");
}

export function init(): void {
  const section = document.getElementById("clients");
  if (!section) return;
  const filter = section.querySelector<HTMLElement>("[data-cl-filter]");
  const wrap = section.querySelector<HTMLElement>("[data-cl-groups]");
  if (!wrap) return;

  const wide = window.matchMedia(WIDE);
  const wallBtn = section.querySelector<HTMLButtonElement>("[data-cl-wall]");
  let active = "all";
  let wallOpen = false;

  const groups: Group[] = Array.from(
    wrap.querySelectorAll<HTMLElement>(".cl__group"),
  ).flatMap((el) => {
    const list = el.querySelector<HTMLElement>(".tiles");
    if (!list) return [];
    return [
      {
        el,
        key: el.getAttribute("data-group") ?? "",
        list,
        tiles: Array.from(list.querySelectorAll<HTMLElement>(".tile")),
        more: el.querySelector<HTMLButtonElement>(".cl__more"),
        expanded: false,
        cols: 0,
      },
    ];
  });

  /** Cap one group to its first rows (in "All", collapsed) or show it whole. */
  const apply = (g: Group): HTMLElement[] => {
    if (g.el.hidden || g.el.classList.contains("is-later")) return [];
    g.cols = columns(g.list);
    const cap = g.cols * ROWS;
    const capped = active === "all" && !g.expanded && g.tiles.length > cap;
    const shown: HTMLElement[] = [];
    g.tiles.forEach((tile, i) => {
      const over = capped && i >= cap;
      if (!over && tile.classList.contains("is-over")) shown.push(tile);
      tile.classList.toggle("is-over", over);
    });
    if (g.more) {
      g.more.hidden = active !== "all" || g.tiles.length <= cap;
      g.more.setAttribute("aria-expanded", String(!capped));
    }
    return shown;
  };

  const wave = (g: Group): void => {
    const cols = g.cols || columns(g.list);
    g.tiles.forEach((tile, i) => {
      const w = Math.floor(i / cols) + (i % cols);
      tile.style.setProperty("--w", String(Math.min(w, 21)));
    });
  };

  /** Fold the groups after the first LEAD in "All" (unless opened). */
  const fold = (): void => {
    const folded = active === "all" && !wallOpen;
    groups.forEach((g, i) => {
      g.el.classList.toggle("is-later", folded && i >= LEAD);
    });
    if (wallBtn) {
      wallBtn.hidden = active !== "all" || groups.length <= LEAD;
      wallBtn.setAttribute("aria-expanded", String(!folded));
    }
  };
  fold();

  wallBtn?.addEventListener("click", () => {
    wallOpen = !wallOpen;
    fold();
    const shown = groups.slice(LEAD).flatMap((g) => {
      apply(g);
      wave(g);
      return g.tiles.filter((t) => !t.classList.contains("is-over"));
    });
    if (wallOpen) {
      popIn(shown.slice(0, 40));
    } else if (wallBtn.getBoundingClientRect().top < 0) {
      // folding from far down: keep the button (and the user) in place
      wallBtn.scrollIntoView({
        behavior: reduced() ? "auto" : "smooth",
        block: "center",
      });
    }
  });

  for (const g of groups) {
    apply(g);
    wave(g);
    g.more?.addEventListener("click", () => {
      g.expanded = !g.expanded;
      const shown = apply(g);
      if (g.expanded) {
        popIn(shown);
      } else {
        // collapsing from far down: bring the group's heading back into view
        const top = g.el.getBoundingClientRect().top;
        if (top < 0) {
          g.el.scrollIntoView({
            behavior: reduced() ? "auto" : "smooth",
            block: "start",
          });
        }
      }
    });
  }

  // re-cap when the column count changes (rotation, resize, the 900px step)
  let pending = false;
  const recap = (): void => {
    if (pending) return;
    pending = true;
    window.requestAnimationFrame(() => {
      pending = false;
      for (const g of groups) {
        const before = g.cols;
        apply(g);
        if (g.cols !== before) wave(g);
      }
    });
  };
  if ("ResizeObserver" in window) new ResizeObserver(recap).observe(wrap);
  else window.addEventListener("resize", recap, { passive: true });
  wide.addEventListener("change", recap);

  /* ── filter chips ── */
  if (!filter) return;
  const chips = Array.from(
    filter.querySelectorAll<HTMLButtonElement>("button[data-group]"),
  );
  filter.hidden = false;

  const swap = (key: string): void => {
    active = key;
    fold();
    for (const g of groups) {
      g.el.hidden = key !== "all" && g.key !== key;
      apply(g);
    }
    const visible = groups
      .filter((g) => !g.el.hidden)
      .flatMap((g) => g.tiles.filter((t) => !t.classList.contains("is-over")));
    // keep the wall's top under the sticky chip row when it has scrolled past
    const head = filter.getBoundingClientRect();
    const top = wrap.getBoundingClientRect().top;
    if (top < head.bottom) {
      window.scrollBy({ top: top - head.bottom - 16, behavior: "auto" });
    }
    popIn(visible.slice(0, 40));
  };

  for (const chip of chips) {
    chip.addEventListener("click", () => {
      const key = chip.getAttribute("data-group") ?? "all";
      if (key === active) return;
      for (const c of chips) {
        c.setAttribute("aria-pressed", String(c === chip));
      }
      // centre the chip in its own row only (scrollIntoView also nudged the
      // page, and the swap's window.scrollBy then cut that smooth scroll
      // short, leaving the pressed chip half off-screen). A delta in physical
      // px works in both directions, whatever the RTL scrollLeft sign.
      const row = chip.parentElement;
      if (row) {
        const r = row.getBoundingClientRect();
        const c = chip.getBoundingClientRect();
        const delta = c.left + c.width / 2 - (r.left + r.width / 2);
        row.scrollBy({
          left: delta,
          behavior: reduced() ? "auto" : "smooth",
        });
      }
      if (reduced()) {
        swap(key);
        return;
      }
      wrap.classList.add("is-out");
      window.setTimeout(() => {
        swap(key);
        wrap.classList.remove("is-out");
      }, 150);
    });
  }
}
